import { useState, useEffect, useCallback, useRef } from 'react';
import { subDays } from 'date-fns';
import { wiseboardService } from '@/services/wiseboardService';
import { useWiseboardRefresh } from '../context/WiseboardRefreshContext';
import type {
  ExtractionHistoryItem,
  ExtractionHistoryFilters,
  ExtractionStatus,
  DatePreset
} from '../types/wiseboard';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useExtractionHistory', 'wiseboard');

const DEFAULT_LIMIT = 50;
const DEBOUNCE_MS = 300;
const REALTIME_DEBOUNCE_MS = 300;
const POLL_INTERVAL_MS = 30_000;

function getDateRangeFromPreset(preset: DatePreset): { start: Date; end: Date } {
  const end = new Date();
  let start: Date;

  switch (preset) {
    case 'today':
      start = new Date(end);
      start.setHours(0, 0, 0, 0);
      break;
    case '7d':
      start = subDays(end, 7);
      break;
    case '30d':
      start = subDays(end, 30);
      break;
    case '90d':
      start = subDays(end, 90);
      break;
    default:
      start = subDays(end, 30);
  }

  return { start, end };
}

export interface UseExtractionHistoryResult {
  items: ExtractionHistoryItem[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  filters: ExtractionHistoryFilters;
  page: number;
  pageSize: number;
  totalPages: number;
  lastRefreshedAt: Date | null;
  setSearch: (search: string) => void;
  setStatus: (status: ExtractionStatus | null) => void;
  setGpu: (gpu: string | null) => void;
  setDatePreset: (preset: DatePreset) => void;
  setCustomDateRange: (start: Date, end: Date) => void;
  setPage: (page: number) => void;
  refresh: () => void;
  clearFilters: () => void;
}

export interface UseExtractionHistoryOptions {
  organizationId?: string;
  enabled?: boolean;
}

export function useExtractionHistory(
  optionsOrOrganizationId?: UseExtractionHistoryOptions | string
): UseExtractionHistoryResult {
  const opts: UseExtractionHistoryOptions =
    typeof optionsOrOrganizationId === 'string'
      ? { organizationId: optionsOrOrganizationId }
      : optionsOrOrganizationId ?? {};
  const organizationId = opts.organizationId;
  const enabled = opts.enabled ?? true;
  const [items, setItems] = useState<ExtractionHistoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_LIMIT);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const realtimePendingRef = useRef<boolean>(false);
  const realtimeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const defaultDateRange = getDateRangeFromPreset('30d');
  const [filters, setFilters] = useState<ExtractionHistoryFilters>({
    search: '',
    status: null,
    gpu: null,
    datePreset: '30d',
    startDate: defaultDateRange.start,
    endDate: defaultDateRange.end
  });

  // Debounce search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(1); // Reset to first page on search change
    }, DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [filters.search]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const offset = (page - 1) * pageSize;

    log.info('Fetching extraction history', {
      page,
      offset,
      limit: pageSize,
      status: filters.status,
      gpu: filters.gpu,
      search: debouncedSearch
    });

    const { data, error: fetchError } = await wiseboardService.getExtractionHistory({
      organizationId,
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      status: filters.status ?? undefined,
      gpu: filters.gpu ?? undefined,
      search: debouncedSearch || undefined,
      limit: pageSize,
      offset
    });

    if (fetchError) {
      log.error('Failed to fetch extraction history', { error: fetchError.message });
      setError(fetchError.message);
      setItems([]);
      setTotalCount(0);
    } else {
      setItems(data);
      // Total count is returned with each row - get from first item
      const count = data[0]?.total_count ?? 0;
      setTotalCount(count);
      log.info('Extraction history fetched', { count: data.length, total: count });
    }

    setLastRefreshedAt(new Date());
    setIsLoading(false);
  }, [organizationId, page, pageSize, filters.startDate, filters.endDate, filters.status, filters.gpu, debouncedSearch]);

  // Real-time subscription + polling, gated on `enabled`
  useEffect(() => {
    if (!enabled) {
      return;
    }

    log.info('Setting up extraction history subscription + polling');

    const channel = wiseboardService.subscribeToDocumentQueue(() => {
      realtimePendingRef.current = true;
      if (!realtimeDebounceRef.current) {
        realtimeDebounceRef.current = setTimeout(() => {
          if (realtimePendingRef.current) {
            log.info('Processing debounced extraction-history realtime burst');
            fetchData();
            realtimePendingRef.current = false;
          }
          realtimeDebounceRef.current = null;
        }, REALTIME_DEBOUNCE_MS);
      }
    });

    // Initial fetch
    fetchData();

    // Slow polling backstop while the panel is open
    const pollInterval = setInterval(() => {
      log.info('Polling extraction history (30s heartbeat)');
      fetchData();
    }, POLL_INTERVAL_MS);

    return () => {
      log.info('Cleaning up extraction history subscription + polling');
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = null;
      }
      clearInterval(pollInterval);
      channel.unsubscribe();
    };
  }, [enabled, fetchData]);

  // Register with the shared Wiseboard refresh signal so the top button reloads us
  // when the panel is enabled (expanded). When collapsed we skip registration so
  // the top refresh doesn't trigger a hidden network call.
  const { registerRefresh } = useWiseboardRefresh();
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return registerRefresh(fetchData);
  }, [enabled, registerRefresh, fetchData]);

  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
  }, []);

  const setStatus = useCallback((status: ExtractionStatus | null) => {
    setFilters(prev => ({ ...prev, status }));
    setPage(1);
  }, []);

  const setGpu = useCallback((gpu: string | null) => {
    setFilters(prev => ({ ...prev, gpu }));
    setPage(1);
  }, []);

  const setDatePreset = useCallback((preset: DatePreset) => {
    const { start, end } = getDateRangeFromPreset(preset);
    setFilters(prev => ({
      ...prev,
      datePreset: preset,
      startDate: start,
      endDate: end
    }));
    setPage(1);
  }, []);

  const setCustomDateRange = useCallback((start: Date, end: Date) => {
    setFilters(prev => ({
      ...prev,
      datePreset: 'custom',
      startDate: start,
      endDate: end
    }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    const defaultRange = getDateRangeFromPreset('30d');
    setFilters({
      search: '',
      status: null,
      gpu: null,
      datePreset: '30d',
      startDate: defaultRange.start,
      endDate: defaultRange.end
    });
    setPage(1);
  }, []);

  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    items,
    totalCount,
    isLoading,
    error,
    filters,
    page,
    pageSize,
    totalPages,
    lastRefreshedAt,
    setSearch,
    setStatus,
    setGpu,
    setDatePreset,
    setCustomDateRange,
    setPage,
    refresh: fetchData,
    clearFilters
  };
}

export default useExtractionHistory;
